import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, FloppyDisk, SpinnerGap, Plus, Trash, ArrowUp, ArrowDown,
  CaretDown, CaretUp, Eye, X, CheckCircle, Circle, DownloadSimple, Star, PencilSimpleLine,
} from '@phosphor-icons/react';

const authHeader = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function CustomerHubFormsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isSuper = user?.role === 'super_admin';
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState(params.get('entity') || (isSuper ? '' : String(user?.entity_id || '')));
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isSuper) {
      axios.get(`${API_URL}/api/entities`, { headers: authHeader() })
        .then((r) => {
          const list = Array.isArray(r.data) ? r.data : [];
          setEntities(list);
          if (list.length && !entityId) setEntityId(String(list[0].id));
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper]);

  const load = useCallback(async (eid) => {
    if (!eid) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/hub/${eid}`, { headers: authHeader() });
      setCfg(r.data);
    } catch (e) {
      toast.error(formatApiError(e, 'Could not load forms'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (entityId) load(entityId); }, [entityId, load]);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/admin/hub/${entityId}`, { forms: cfg.forms }, { headers: authHeader() });
      setCfg(r.data);
      toast.success('Forms saved');
    } catch (e) {
      toast.error(formatApiError(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="customer-hub-forms-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="flex items-start gap-3">
          <button onClick={() => navigate(`/customer-hub${entityId ? `?entity=${entityId}` : ''}`)} className="w-10 h-10 rounded-lg border border-[#EDE6D8] bg-white flex items-center justify-center hover:bg-stone-50 shrink-0" data-testid="forms-back">
            <ArrowLeft size={18} className="text-[#0A2540]" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#0A2540] flex items-center gap-2"><PencilSimpleLine size={24} weight="duotone" />Form Builder</h1>
            <p className="text-sm text-stone-500 mt-1">Craft the "Report a concern" and "Give feedback" screens{cfg?.entity_name ? ` — ${cfg.entity_name}` : ''}.</p>
          </div>
        </div>
        {isSuper && (
          <select value={entityId} onChange={(e) => setEntityId(e.target.value)} data-testid="forms-entity-select"
            className="h-10 px-3 rounded-lg border border-[#EDE6D8] bg-white text-sm text-[#1C1917] focus:outline-none focus:border-[#0A2540]">
            {entities.map((e) => <option key={e.id} value={e.id}>{e.entity_name}</option>)}
          </select>
        )}
      </div>

      {loading || !cfg ? (
        <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0A2540]" /></div>
      ) : (
        <>
          <HubFormsEditor cfg={cfg} setCfg={setCfg} entityId={entityId} />
          <div className="flex justify-end mt-6">
            <button onClick={save} disabled={saving} data-testid="forms-save"
              className="h-11 px-6 rounded-xl text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #0A2540 0%, #0991d6 100%)' }}>
              {saving ? <SpinnerGap size={17} className="animate-spin" /> : <FloppyDisk size={17} />}Save forms
            </button>
          </div>
        </>
      )}
    </div>
  );
}


const FIELD_TYPES = [
  { v: 'short_text', label: 'Short text' },
  { v: 'long_text', label: 'Long text' },
  { v: 'dropdown', label: 'Dropdown' },
  { v: 'rating', label: 'Rating (1–5)' },
  { v: 'yesno', label: 'Yes / No' },
];
const newQid = () => Math.random().toString(36).slice(2, 10);
const LBL = 'text-[11px] font-semibold text-stone-500 uppercase tracking-wider';
const INP = 'w-full h-10 px-3 rounded-lg border border-[#EDE6D8] text-sm focus:outline-none focus:border-[#0A2540]';

function HubFormsEditor({ cfg, setCfg, entityId }) {
  const forms = cfg.forms || {};
  const concern = forms.concern || {};
  const feedbackForms = forms.feedback_forms || [];
  const [openIdx, setOpenIdx] = useState(-1);
  const [responsesFor, setResponsesFor] = useState(null);

  const patchForms = (patch) => setCfg((c) => ({ ...c, forms: { ...(c.forms || {}), ...patch } }));
  const setConcern = (p) => patchForms({ concern: { ...((cfg.forms || {}).concern || {}), ...p } });

  const setFbList = (updater) => setCfg((c) => {
    const list = (c.forms || {}).feedback_forms || [];
    return { ...c, forms: { ...(c.forms || {}), feedback_forms: updater(list) } };
  });
  const updateForm = (i, p) => setFbList((list) => list.map((f, idx) => (idx === i ? { ...f, ...p } : f)));
  const addForm = () => {
    setFbList((list) => {
      const anyPublished = list.some((f) => f.published);
      return [...list, {
        id: newQid(), name: `Feedback form ${list.length + 1}`, title: 'Give feedback',
        intro: '', thank_you: 'Your feedback helps us serve you better.',
        csat_enabled: true, nps_enabled: true, comment_enabled: true,
        questions: [], published: !anyPublished, response_count: 0,
      }];
    });
    setOpenIdx(feedbackForms.length);
  };
  const delForm = (i) => {
    if (!window.confirm('Delete this feedback form? Already-collected responses are retained and will roll up under your first feedback form.')) return;
    setFbList((list) => list.filter((_, idx) => idx !== i));
    setOpenIdx(-1);
  };
  const togglePublish = (i, on) => setFbList((list) => list.map((f, idx) => ({ ...f, published: on ? idx === i : (idx === i ? false : f.published) })));

  return (
    <section className="rounded-2xl border border-[#EDE6D8] bg-white p-5" data-testid="hub-forms-editor">
      <FormEditor tp="concern" title="Report a concern" form={concern} onPatch={setConcern} isConcern />

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-bold text-[#0A2540]">Feedback forms</h3>
            <p className="text-[11px] text-stone-400">Create several surveys — only the <b>published</b> one is shown to customers.</p>
          </div>
          <button type="button" onClick={addForm} className="h-9 px-3 rounded-lg border border-[#EDE6D8] text-sm font-medium text-[#0991d6] hover:bg-stone-50 flex items-center gap-1.5" data-testid="feedback-add-form"><Plus size={15} />Add form</button>
        </div>

        {feedbackForms.length === 0 && <p className="text-xs text-stone-400 py-2">No feedback forms yet. Add one to start collecting feedback.</p>}

        <div className="space-y-2">
          {feedbackForms.map((f, i) => (
            <div key={f.id || i} className="rounded-xl border border-[#EDE6D8]" data-testid={`feedback-form-card-${i}`}>
              <div className="flex items-center gap-2 p-3">
                <button type="button" onClick={() => setOpenIdx(openIdx === i ? -1 : i)} className="w-8 h-8 rounded-lg border border-[#EDE6D8] flex items-center justify-center hover:bg-stone-50 shrink-0" data-testid={`feedback-expand-${i}`}>
                  {openIdx === i ? <CaretUp size={15} /> : <CaretDown size={15} />}
                </button>
                <input value={f.name || ''} onChange={(e) => updateForm(i, { name: e.target.value })} placeholder="Form name" className="flex-1 h-9 px-2.5 rounded-lg border border-transparent hover:border-[#EDE6D8] focus:border-[#0A2540] text-sm font-medium text-[#0A2540] focus:outline-none min-w-0" data-testid={`feedback-name-${i}`} />
                {f.published
                  ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#DCEBE2] text-[#2F6D4B] text-[10px] font-semibold uppercase tracking-wider shrink-0" data-testid={`feedback-published-badge-${i}`}><CheckCircle size={12} weight="fill" />Live</span>
                  : <span className="text-[10px] text-stone-400 uppercase tracking-wider shrink-0">Draft</span>}
                <button type="button" onClick={() => togglePublish(i, !f.published)} title={f.published ? 'Unpublish' : 'Publish'} data-testid={`feedback-publish-${i}`}
                  className={`h-8 px-2.5 rounded-lg text-xs font-semibold flex items-center gap-1 shrink-0 border ${f.published ? 'border-[#2F6D4B]/30 text-[#2F6D4B] bg-[#DCEBE2]/40 hover:bg-[#DCEBE2]' : 'border-[#EDE6D8] text-stone-500 hover:bg-stone-50'}`}>
                  {f.published ? <CheckCircle size={14} weight="fill" /> : <Circle size={14} />}{f.published ? 'Published' : 'Publish'}
                </button>
                <button type="button" onClick={() => setResponsesFor(f)} className="h-8 px-2.5 rounded-lg border border-[#EDE6D8] text-xs font-medium text-[#0A2540] hover:bg-stone-50 flex items-center gap-1 shrink-0" data-testid={`feedback-responses-${i}`}>
                  <Eye size={14} />Responses{typeof f.response_count === 'number' ? ` (${f.response_count})` : ''}
                </button>
                <button type="button" onClick={() => delForm(i)} className="w-8 h-8 rounded-lg border border-[#EDE6D8] text-[#B4453A] flex items-center justify-center hover:bg-red-50 shrink-0" data-testid={`feedback-delete-${i}`}><Trash size={15} /></button>
              </div>
              {openIdx === i && (
                <div className="border-t border-[#EDE6D8] p-3">
                  <FormEditor tp={`fbform-${i}`} title={null} form={f} onPatch={(p) => updateForm(i, p)} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {responsesFor && <ResponsesModal entityId={entityId} form={responsesFor} onClose={() => setResponsesFor(null)} />}
    </section>
  );
}

function FormEditor({ tp, title, form, onPatch, isConcern }) {
  const questions = form.questions || [];
  const cats = form.categories || [];
  const [newCat, setNewCat] = useState('');
  const patch = (p) => onPatch(p);
  const setQ = (i, p) => patch({ questions: questions.map((q, idx) => (idx === i ? { ...q, ...p } : q)) });
  const addQ = () => patch({ questions: [...questions, { id: newQid(), label: '', type: 'short_text', required: false, options: [] }] });
  const delQ = (i) => patch({ questions: questions.filter((_, idx) => idx !== i) });
  const moveQ = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= questions.length) return;
    const arr = [...questions];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    patch({ questions: arr });
  };
  const addCat = () => {
    const v = newCat.trim().toLowerCase();
    if (v && !cats.includes(v)) patch({ categories: [...cats, v] });
    setNewCat('');
  };
  const delCat = (c) => patch({ categories: cats.filter((x) => x !== c) });

  return (
    <div className={title ? 'rounded-xl border border-[#EDE6D8] p-4' : ''} data-testid={`form-editor-${tp}`}>
      {title && <h3 className="text-sm font-bold text-[#0A2540] mb-3">{title}</h3>}
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className={LBL}>Title</label><input className={INP} value={form.title || ''} onChange={(e) => patch({ title: e.target.value })} data-testid={`${tp}-title`} /></div>
        <div><label className={LBL}>Intro text</label><input className={INP} value={form.intro || ''} onChange={(e) => patch({ intro: e.target.value })} data-testid={`${tp}-intro`} /></div>
      </div>
      <div className="mt-3"><label className={LBL}>Thank-you message</label><input className={INP} value={form.thank_you || ''} onChange={(e) => patch({ thank_you: e.target.value })} data-testid={`${tp}-thankyou`} /></div>

      {isConcern && (
        <div className="mt-4">
          <label className={LBL}>Concern categories</label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {cats.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#EEF4F8] text-[#0A2540] text-xs font-medium" data-testid={`cat-chip-${c}`}>
                {c}<button type="button" onClick={() => delCat(c)} className="text-stone-400 hover:text-[#B4453A]"><Trash size={12} /></button>
              </span>
            ))}
            {cats.length === 0 && <span className="text-xs text-stone-400">No categories yet.</span>}
          </div>
          <div className="flex gap-2 mt-2">
            <input className={INP} value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCat(); } }} placeholder="Add a category…" data-testid="cat-input" />
            <button type="button" onClick={addCat} className="h-10 px-3 rounded-lg border border-[#EDE6D8] text-sm font-medium text-[#0991d6] hover:bg-stone-50 shrink-0" data-testid="cat-add">Add</button>
          </div>
        </div>
      )}

      {!isConcern && (
        <div className="mt-4 flex flex-wrap gap-4">
          {[['csat_enabled', 'Star rating'], ['nps_enabled', 'NPS (0–10)'], ['comment_enabled', 'Comment box']].map(([k, lbl]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-[#1C1917]">
              <input type="checkbox" checked={form[k] !== false} onChange={(e) => patch({ [k]: e.target.checked })} className="w-4 h-4 accent-[#0A2540]" data-testid={`${tp}-${k}`} />{lbl}
            </label>
          ))}
        </div>
      )}

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <label className={LBL}>Custom questions</label>
          <button type="button" onClick={addQ} className="text-sm font-medium text-[#0991d6] flex items-center gap-1 hover:underline" data-testid={`${tp}-add-q`}><Plus size={14} />Add question</button>
        </div>
        {questions.length === 0 && <p className="text-xs text-stone-400">No custom questions. Add one to collect more from customers.</p>}
        <div className="space-y-3">
          {questions.map((q, i) => (
            <div key={q.id} className="rounded-lg border border-[#EDE6D8] p-3 bg-[#FCFAF6]" data-testid={`${tp}-q-row-${i}`}>
              <div className="flex gap-2">
                <input className={INP} value={q.label} onChange={(e) => setQ(i, { label: e.target.value })} placeholder="Question label" data-testid={`${tp}-q-label-${i}`} />
                <select className="h-10 px-2 rounded-lg border border-[#EDE6D8] text-sm shrink-0" value={q.type} onChange={(e) => setQ(i, { type: e.target.value })} data-testid={`${tp}-q-type-${i}`}>
                  {FIELD_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
              </div>
              {q.type === 'dropdown' && (
                <input className={INP + ' mt-2'} value={(q.options || []).join(', ')} onChange={(e) => setQ(i, { options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="Options, comma-separated" data-testid={`${tp}-q-options-${i}`} />
              )}
              <div className="flex items-center justify-between mt-2">
                <label className="flex items-center gap-2 text-xs text-stone-600">
                  <input type="checkbox" checked={!!q.required} onChange={(e) => setQ(i, { required: e.target.checked })} className="w-4 h-4 accent-[#0A2540]" data-testid={`${tp}-q-req-${i}`} />Required
                </label>
                <div className="flex gap-1">
                  <button type="button" onClick={() => moveQ(i, -1)} disabled={i === 0} className="w-8 h-8 rounded-lg border border-[#EDE6D8] flex items-center justify-center disabled:opacity-30 hover:bg-white" data-testid={`${tp}-q-up-${i}`}><ArrowUp size={14} /></button>
                  <button type="button" onClick={() => moveQ(i, 1)} disabled={i === questions.length - 1} className="w-8 h-8 rounded-lg border border-[#EDE6D8] flex items-center justify-center disabled:opacity-30 hover:bg-white" data-testid={`${tp}-q-down-${i}`}><ArrowDown size={14} /></button>
                  <button type="button" onClick={() => delQ(i)} className="w-8 h-8 rounded-lg border border-[#EDE6D8] text-[#B4453A] flex items-center justify-center hover:bg-red-50" data-testid={`${tp}-q-del-${i}`}><Trash size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ResponsesModal({ entityId, form, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/admin/hub/${entityId}/feedback/${form.id}/responses`, { headers: authHeader() })
      .then((r) => setData(r.data))
      .catch(() => setData({ responses: [], total: 0, questions: [] }))
      .finally(() => setLoading(false));
  }, [entityId, form.id]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/hub/${entityId}/feedback/${form.id}/responses.xlsx`, { headers: authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `feedback-${(form.name || 'responses').replace(/\s+/g, '-').toLowerCase()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
    finally { setDownloading(false); }
  };

  const joinAnswers = (arr) => (arr || []).map((a) => `${a.label}: ${Array.isArray(a.value) ? a.value.join(', ') : a.value}`).join(' · ');
  const rows = data?.responses || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="feedback-responses-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 p-5 border-b border-[#EDE6D8]">
          <div className="min-w-0">
            <h3 className="font-bold text-[#0A2540] truncate">{form.name || 'Feedback'} — responses</h3>
            <p className="text-xs text-stone-500">{loading ? 'Loading…' : `${data?.total ?? 0} total${(data?.returned ?? 0) < (data?.total ?? 0) ? ` · showing latest ${data?.returned}` : ''}`}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={download} disabled={downloading || (data?.total ?? 0) === 0} data-testid="feedback-responses-download"
              className="h-9 px-3 rounded-lg text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #0A2540 0%, #0991d6 100%)' }}>
              {downloading ? <SpinnerGap size={15} className="animate-spin" /> : <DownloadSimple size={15} />}Excel
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-lg border border-[#EDE6D8] flex items-center justify-center hover:bg-stone-50" data-testid="feedback-responses-close"><X size={16} /></button>
          </div>
        </div>
        <div className="overflow-auto p-1">
          {loading ? (
            <div className="flex justify-center py-16"><SpinnerGap size={26} className="animate-spin text-[#0A2540]" /></div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-stone-400 text-center py-16" data-testid="feedback-responses-empty">No responses collected yet for this form.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#FCFAF6]">
                <tr className="text-left text-[10px] uppercase tracking-wider text-stone-400 border-b border-[#EDE6D8]">
                  <th className="py-2 px-3 font-semibold">When</th>
                  <th className="py-2 px-3 font-semibold">CSAT</th>
                  <th className="py-2 px-3 font-semibold">NPS</th>
                  <th className="py-2 px-3 font-semibold">Comment / answers</th>
                  <th className="py-2 px-3 font-semibold">Name</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-[#F4F0E8] last:border-0 align-top" data-testid={`feedback-response-row-${i}`}>
                    <td className="py-2 px-3 text-stone-600 whitespace-nowrap">{r.created_at || '—'}</td>
                    <td className="py-2 px-3">{r.csat != null ? <span className="inline-flex items-center gap-0.5 text-[#E0A82E] font-semibold">{r.csat}<Star size={12} weight="fill" /></span> : '—'}</td>
                    <td className="py-2 px-3 text-[#0A2540] font-medium">{r.nps != null ? r.nps : '—'}</td>
                    <td className="py-2 px-3 text-[#1C1917]">
                      {r.comment && <div>{r.comment}</div>}
                      {(r.answers || []).length > 0 && <div className="text-xs text-stone-500 mt-0.5">{joinAnswers(r.answers)}</div>}
                      {!r.comment && (r.answers || []).length === 0 && <span className="text-stone-300">—</span>}
                    </td>
                    <td className="py-2 px-3 text-stone-600 whitespace-nowrap">{r.name || '—'}{r.campus ? <div className="text-[11px] text-stone-400">{r.campus}</div> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
